import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineMenu } from 'react-icons/ai';
import { FiSettings, FiLock } from 'react-icons/fi';
import { FaSun, FaMoon } from 'react-icons/fa';
import { MdOutlineHelpOutline } from 'react-icons/md';
import { RiNotification3Line } from 'react-icons/ri';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import avatar from '../data/avatar.jpg';
import { Help, Notification, UserProfile } from '.';
import { useStateContext } from '../contexts/ContextProvider';
import { useAuth } from '../contexts/AuthContext';
import { useXMPP } from '../contexts/XMPPContext';
import { getThemeNames, setTheme as setGlobalTheme, getCurrentThemeColors } from '../config/themes';
import { apiCall } from '../config/api';
import { User, NavButtonProps } from '../types';

const LIGHT_THEME = 'ClinicalOutcomes';
const DARK_THEME = 'ClinicalOutcomesDark';

interface NavButtonProps {
  title: string;
  customFunc: () => void;
  icon: React.ReactNode;
  dotColor?: string;
}

interface NavButtonProps {
  title: string;
  customFunc: () => void;
  icon: React.ReactNode;
  dotColor?: string;
}

const NavButton: React.FC<NavButtonProps> = ({ title, customFunc, icon, dotColor }) => (
  <TooltipComponent
    content={title}
    position="BottomCenter"
    cssClass="syncfusion-tooltip"
  >
    <button
      type="button"
      onClick={customFunc}
      className="relative text-xl rounded-full p-3 hover:bg-base-200 text-base-content"
    >
      <span className="absolute inline-flex rounded-full h-2 w-2 right-2 top-2 bg-primary" />
      {icon}
    </button>
  </TooltipComponent>
);

interface SearchResultProps {
  result: any; // TODO: Define proper search result type
  onClick: (result: any) => void;
}

const SearchResult: React.FC<SearchResultProps> = ({ result, onClick }) => {
  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div
      onClick={onClick}
      className="p-3 hover:bg-base-200 cursor-pointer border-b border-base-200 last:border-0"
    >
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <span className="font-medium">
            {result.first_name} {result.last_name}
          </span>
          <div className="text-sm text-base-content/60 space-x-2">
            <span>DOB: {formatDate(result.date_of_birth)}</span>
            <span>•</span>
            <span>MRN: {result.mrn}</span>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
            Patient
          </span>
          {result.is_active ? (
            <span className="text-xs text-success mt-1">Active</span>
          ) : (
            <span className="text-xs text-error mt-1">Inactive</span>
          )}
        </div>
      </div>
    </div>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || LIGHT_THEME);
  const themes = getThemeNames();
  const { user, token, lockScreen } = useAuth(); // Get user and token from auth context
  const { status: xmppStatus } = useXMPP(); // Get XMPP status

  // Generate user initials and display name. Prefers the backend-assigned
  // initials (the "duogram" used for audit-log attribution -- see
  // authentication.models.generate_unique_initials) so this matches what
  // shows up as "Booked by PV" etc. elsewhere in the system.
  const getInitials = (user: User | null): string => {
    if (!user) return 'AU';
    if (user.initials) return user.initials.toUpperCase();
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return (user.username || user.email || 'AU').substring(0, 2).toUpperCase();
  };

  const getDisplayName = (user: User | null): string => {
    if (!user) return 'Admin';
    if (user.first_name) {
      return user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1);
    }
    return user.username || user.email || 'User';
  };

  const userInitials = getInitials(user);
  const displayName = getDisplayName(user);

  const {
    activeMenu,
    setActiveMenu,
    sidebarState,
    setSidebarState,
    isClicked,
    handleClick,
    screenSize,
    setScreenSize,
    setThemeSettings,
    setCurrentTheme,
    isDarkTheme,
    isAdminUser,
    selectedClientId,
    setSelectedClientId,
    selectedFacilityId,
    setSelectedFacilityId,
    availableClients,
    visibleFacilities,
  } = useStateContext();

  const handleMenuToggle = () => {
    if (screenSize && screenSize <= 900) {
      // Mobile: toggle between hidden and full
      setSidebarState(sidebarState === 'hidden' ? 'full' : 'hidden');
    } else {
      // Desktop: cycle through full -> collapsed -> full
      setSidebarState(sidebarState === 'full' ? 'collapsed' : 'full');
    }
  };

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (screenSize && screenSize <= 900) {
      setSidebarState('hidden');
    } else {
      setSidebarState('full');
    }
  }, [screenSize]);

  useEffect(() => {
    setGlobalTheme(theme);
    const themeColors = getCurrentThemeColors();
    document.documentElement.style.setProperty('--sf-tooltip-bg', themeColors.primary);
    document.documentElement.style.setProperty('--sf-tooltip-text', themeColors.base100);
  }, [theme]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const searchPatients = async () => {
      if (searchTerm.length >= 2 && token) {
        try {
          const response = await apiCall(`/patients/?search=${encodeURIComponent(searchTerm)}`, {
            method: 'GET',
          }, token);
          
          if (response.ok) {
            const data = await response.json();
            // Handle both paginated and non-paginated responses
            const patients = data.results || data || [];
            
            // Additional client-side filtering to match typed text more precisely
            const searchLower = searchTerm.toLowerCase().trim();
            const filtered = patients.filter((patient: any) => {
              const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
              const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
              const mrn = (patient.medical_record_number || patient.mrn || '').toLowerCase();
              
              return fullName.includes(searchLower) || 
                     reverseName.includes(searchLower) || 
                     mrn.includes(searchLower);
            });
            
            setSearchResults(filtered);
            setShowResults(filtered.length > 0);
          } else {
            console.error('Search failed:', response.status);
            setSearchResults([]);
          }
        } catch (error) {
          console.error('Error searching patients:', error);
          setSearchResults([]);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };

    const debounceTimer = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, token]);

  const handleResultClick = (result: any) => { // TODO: Define proper result type
    setSearchTerm('');
    setShowResults(false);
    navigate(`/patients/${result.id}`);
  };

  const handleThemeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newTheme = event.target.value;
    setTheme(newTheme);
    setGlobalTheme(newTheme);
    setCurrentTheme(newTheme); // Update context so DocumentEditor can react
  };

  return (
    <div className="flex justify-between p-2 relative bg-base-100">
      {/* Left section */}
      <div className="flex items-center">
        <NavButton
          title="Menu"
          customFunc={handleMenuToggle}
          icon={<AiOutlineMenu />}
        />
      </div>

      {/* Center section - Search */}
      <div className="flex-1 max-w-xl mx-8" ref={searchRef}>
        <div className="relative">
          <input
            type="text"
            placeholder="Search patients, documents, or correspondence..."
            className="input input-bordered w-full pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg className="w-4 h-4 text-base-content/50" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
            </svg>
          </div>

          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto divide-y divide-base-200">
              <div className="p-2 bg-base-200/50 text-sm font-medium">
                Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </div>
              {searchResults.map((result) => (
                <SearchResult
                  key={result.id}
                  result={result}
                  onClick={() => handleResultClick(result)}
                />
              ))}
            </div>
          )}

          {/* No Results Message */}
          {showResults && searchTerm.length >= 2 && searchResults.length === 0 && (
            <div className="absolute w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg z-50 p-4 text-center text-base-content/60">
              No results found for "{searchTerm}"
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:flex items-center gap-3 mr-4 shrink-0 min-w-[36rem] justify-end">
        {isAdminUser && (
          <div className="flex items-center gap-2 w-[17rem]">
            <span className="font-bold text-sm whitespace-nowrap w-14">Client</span>
            <select
              className="select select-bordered h-10 min-h-10 w-[13rem]"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              {availableClients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2 w-[18rem]">
          <span className="font-bold text-sm whitespace-nowrap w-14">Facility</span>
          <select
            className="select select-bordered h-10 min-h-10 w-[14rem]"
            value={selectedFacilityId}
            onChange={(e) => setSelectedFacilityId(e.target.value)}
          >
            {visibleFacilities.map((facility) => (
              <option key={facility.id} value={facility.id}>{facility.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2">
        <NavButton
          title="Tasks"
          customFunc={() => navigate('/tasks')}
          icon={<span className="material-symbols-outlined text-xl">task</span>}
        />

        <NavButton
          title="Help"
          customFunc={() => window.open('http://localhost:3001/', '_blank')}
          icon={<MdOutlineHelpOutline />}
        />

        <NavButton
          title="Alerts"
          dotColor={true}
          customFunc={() => handleClick('notification')}
          icon={<RiNotification3Line />}
        />

        {/* Quick light/dark toggle -- swaps between the two default CO themes.
            Full theme picker (all named themes) stays behind the gear icon. */}
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setCurrentTheme(isDarkTheme ? LIGHT_THEME : DARK_THEME)}
          title={isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkTheme ? <FaSun size={16} /> : <FaMoon size={16} />}
        </button>

        <TooltipComponent content="Theme" position="BottomCenter">
          <button className="btn btn-ghost btn-sm" onClick={() => setThemeSettings(true)}>
            <FiSettings size={18} />
          </button>
        </TooltipComponent>

        <TooltipComponent content="Lock screen" position="BottomCenter">
          <button className="btn btn-ghost btn-sm" onClick={lockScreen} aria-label="Lock screen">
            <FiLock size={17} />
          </button>
        </TooltipComponent>

        <TooltipComponent content="Profile" position="BottomCenter">
          <div
            className="flex items-center gap-2 cursor-pointer p-1 hover:bg-base-200 rounded-lg text-base-content"
            onClick={() => handleClick('userProfile')}
          >
            {/* Display user initials with status indicator */}
            <div className="relative">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">{userInitials}</span>
              </div>
              {/* XMPP status indicator - color changes based on status */}
              <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-base-100 ${
                xmppStatus === 'online' ? 'bg-success' :
                xmppStatus === 'away' ? 'bg-warning' :
                xmppStatus === 'dnd' ? 'bg-error' :
                'bg-base-300'
              }`}></div>
            </div>
            <div className="flex flex-col">
              <p className="text-sm">
                <span>Hi,</span>{' '}
                <span className="font-bold ml-1">
                  {user?.first_name 
                    ? `${user.first_name}!` 
                    : (user as any)?.username || 'User'}
                </span>
              </p>
            </div>
            <MdKeyboardArrowDown className="text-lg" />
          </div>
        </TooltipComponent>

        {isClicked.notification && <Notification />}
        {isClicked.userProfile && <UserProfile />}
      </div>
    </div>
  );
};

export default Navbar;